// Helpers for the read-only code artifact (sidebar + full-screen viewer).
// Binary-looking "code" extensions stay on the download path.

const BINARY_CODE_EXTS = new Set(['class', 'jar']);

const LANGUAGE_BY_EXT = {
  js: 'javascript',
  jsx: 'jsx',
  mjs: 'javascript',
  cjs: 'javascript',
  ts: 'typescript',
  tsx: 'tsx',
  py: 'python',
  pyw: 'python',
  rb: 'ruby',
  go: 'go',
  rs: 'rust',
  java: 'java',
  kt: 'kotlin',
  kts: 'kotlin',
  swift: 'swift',
  c: 'c',
  h: 'c',
  cpp: 'cpp',
  cc: 'cpp',
  hpp: 'cpp',
  hh: 'cpp',
  cs: 'csharp',
  php: 'php',
  css: 'css',
  scss: 'scss',
  less: 'less',
  html: 'markup',
  htm: 'markup',
  xml: 'markup',
  json: 'json',
  jsonl: 'json',
  ndjson: 'json',
  yml: 'yaml',
  yaml: 'yaml',
  toml: 'toml',
  md: 'markdown',
  markdown: 'markdown',
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',
  sql: 'sql',
  graphql: 'graphql',
  gql: 'graphql',
  diff: 'diff',
  patch: 'diff',
  vue: 'markup',
  svelte: 'markup',
  dockerfile: 'docker',
  txt: 'text',
  env: 'text',
  ini: 'ini',
  conf: 'ini',
  lua: 'lua',
  r: 'r',
  dart: 'dart',
  scala: 'scala',
  pl: 'perl',
  pm: 'perl',
  ex: 'elixir',
  exs: 'elixir',
  hs: 'haskell',
  proto: 'protobuf',
  tf: 'hcl',
  tfvars: 'hcl',
  hcl: 'hcl',
};

const TEXT_MIME_PREFIXES = ['text/'];
const TEXT_MIMES = new Set([
  'application/json',
  'application/javascript',
  'application/x-javascript',
  'application/typescript',
  'application/x-ndjson',
  'application/xml',
]);

export const MAX_CODE_PREVIEW_BYTES = 1_500_000;
export const HIGHLIGHT_CHUNK_LINES = 200;

export function codeFileExtension(file) {
  const candidates = [
    String(file?.extension || '').replace(/^\./, '').toLowerCase(),
    ...[file?.name, file?.originalName].map((name) => {
      const raw = String(name || '');
      const dotted = raw.split('.').pop();
      if (!dotted || dotted === raw) return '';
      return dotted.toLowerCase();
    }),
  ].filter(Boolean);
  return candidates.find((ext) => LANGUAGE_BY_EXT[ext] || BINARY_CODE_EXTS.has(ext))
    || candidates[0]
    || '';
}

export function languageFromCodeFile(file) {
  return LANGUAGE_BY_EXT[codeFileExtension(file)] || 'text';
}

const LANGUAGE_DISPLAY = {
  html: 'HTML',
  htm: 'HTML',
  xml: 'XML',
  vue: 'Vue',
  svelte: 'Svelte',
  js: 'JavaScript',
  jsx: 'JSX',
  mjs: 'JavaScript',
  cjs: 'JavaScript',
  ts: 'TypeScript',
  tsx: 'TSX',
  py: 'Python',
  md: 'Markdown',
  markdown: 'Markdown',
  sh: 'Bash',
  bash: 'Bash',
  zsh: 'Bash',
  txt: 'Text',
};

export function languageDisplayName(file) {
  const ext = codeFileExtension(file);
  if (LANGUAGE_DISPLAY[ext]) return LANGUAGE_DISPLAY[ext];
  const lang = languageFromCodeFile(file);
  if (lang === 'markup') return ext ? ext.toUpperCase() : 'HTML';
  if (lang === 'javascript') return 'JavaScript';
  if (lang === 'typescript') return 'TypeScript';
  if (!lang || lang === 'text') return 'Text';
  return lang.charAt(0).toUpperCase() + lang.slice(1);
}

export function isHtmlCodeFile(file) {
  if (!file) return false;
  const mime = String(file.mimeType || file.type || '').toLowerCase();
  return ['html', 'htm'].includes(codeFileExtension(file)) || mime === 'text/html';
}

export function isMarkdownCodeFile(file) {
  if (!file) return false;
  const mime = String(file.mimeType || file.type || '').toLowerCase();
  return ['md', 'markdown'].includes(codeFileExtension(file))
    || mime === 'text/markdown'
    || mime === 'text/x-markdown';
}

export function isCodeFile(file) {
  if (!file) return false;
  const ext = codeFileExtension(file);
  if (BINARY_CODE_EXTS.has(ext)) return false;
  const fileType = String(file.fileType || file.type || '').toLowerCase();
  if (fileType === 'code') return true;
  if (LANGUAGE_BY_EXT[ext]) return true;
  const mime = String(file.mimeType || file.type || '').toLowerCase();
  if (mime === 'text/csv' || mime === 'text/tab-separated-values') return false;
  if (TEXT_MIME_PREFIXES.some((prefix) => mime.startsWith(prefix))) return true;
  return TEXT_MIMES.has(mime);
}

export function looksLikeBinaryText(text) {
  const sample = String(text || '').slice(0, 8192);
  return sample.includes('\u0000');
}

export function codeFileIsTooLarge(file) {
  const bytes = Number(file?.sizeInBytes || file?.size || 0);
  return Number.isFinite(bytes) && bytes > MAX_CODE_PREVIEW_BYTES;
}

// Generated apps need to export user-created CSVs and other files. Keep their
// origin opaque so download support does not grant access to Gab AI's storage.
// FPS games need pointer lock for mouse look; this does not grant origin access.
export const HTML_PREVIEW_SANDBOX = 'allow-forms allow-popups allow-scripts allow-downloads allow-pointer-lock';

export const HTML_PREVIEW_CSP = "default-src 'self' 'unsafe-inline' 'unsafe-eval' https: data: blob:; object-src 'none'; base-uri 'none';";

export function injectHtmlPreviewCsp(rawHtml) {
  if (typeof rawHtml !== 'string' || !rawHtml) return '';
  const cspMeta = `<meta http-equiv="Content-Security-Policy" content="${HTML_PREVIEW_CSP}">`;
  if (/<head[^>]*>/i.test(rawHtml)) {
    return rawHtml.replace(/<head[^>]*>/i, `$&\n  ${cspMeta}`);
  }
  if (/<html[^>]*>/i.test(rawHtml)) {
    return rawHtml.replace(/<html[^>]*>/i, `$&\n<head>\n  ${cspMeta}\n</head>`);
  }
  return `${cspMeta}\n${rawHtml}`;
}
